import React from 'react';
import cx from 'classnames';

import PageLayout from '@magoosh/layouts/page';
import { PageProps } from '@magoosh/layouts/types';
import { formatPrice } from 'utilities/money';

import SectionHeader from '@marketing/components/section_header';
import FAQSection from '@marketing/sections/faq';
import { OfferProps } from '@marketing/types';
import { defaultRegistrationUrl } from '@marketing/utilities';

import OnDemandClassesSection from './sections/on_demand_classes';
import TestimonialsSection from './sections/testimonials';

import CompetitorComparisonSection from '../sections/competitor_comparison';
import HeroSection from '../sections/hero';
import OffersSection from '../sections/offers';
import StartYourPrepSection from '../sections/start_your_prep';

import AITutorIcon from '@marketing/sections/value_props/assets/green/banner_check_icon.svg';
import PracticeQuestionsIcon from '@marketing/sections/value_props/assets/green/practice_questions_icon.svg';
import PracticeTestsIcon from '@marketing/sections/value_props/assets/green/practice_tests_icon.svg';
import ScorePredictorIcon from '@marketing/sections/value_props/assets/green/score_gauge_icon.svg';
import StudySchedulesIcon from '@marketing/sections/value_props/assets/green/timely_guidance_icon.svg';
import VideoLessonsIcon from '@marketing/sections/value_props/assets/green/video_lessons_icon.svg';

import BannerCheckIcon from '../sections/competitor_comparison/assets/banner_check_icon.svg';
import BubbleQuestionIcon from '../sections/competitor_comparison/assets/bubble_question_icon.svg';
import ChecklistIcon from '../sections/competitor_comparison/assets/checklist_icon.svg';
import CircleRefreshIcon from '../sections/competitor_comparison/assets/circle_refresh_icon.svg';
import DollarSignIcon from '../sections/competitor_comparison/assets/dollar_sign_icon.svg';
import FireworkIcon from '../sections/competitor_comparison/assets/firework_icon.svg';
import FlashcardsIcon from '../sections/competitor_comparison/assets/flashcards_icon.svg';
import HighFiveIcon from '../sections/competitor_comparison/assets/high_five_icon.svg';
import HourglassIcon from '../sections/competitor_comparison/assets/hourglass_icon.svg';
import MobileDeviceIcon from '../sections/competitor_comparison/assets/mobile_device_icon.svg';
import NotebookIcon from '../sections/competitor_comparison/assets/notebook_icon.svg';
import ScoreGaugeIcon from '../sections/competitor_comparison/assets/score_gauge_icon.svg';
import StarIcon from '../sections/competitor_comparison/assets/star_icon.svg';
import VideoPlayerIcon from '../sections/competitor_comparison/assets/video_player_icon.svg';

import styles from '../style.module.scss';
import marketingStyles from '@marketing/style.module.scss';

interface Props extends PageProps {
  hideSignUp: boolean;
  isLoggedIn: boolean;
  offers: OfferProps[];
}

export default (props: Props) => {
  const onDemandClassesOffer = props.offers.find((offer) => offer.slug === 'premium-live-classes');
  const premiumOffer = props.offers.find((offer) => offer.slug === 'premium-12-months');
  const trialOffer = props.offers.find((offer) => offer.slug === 'trial');

  return (
    <div style={{ overflow: 'hidden', position: 'relative' }}>
      {/* Absolutely positioned background elements */}
      <>
        <div
          className={marketingStyles.greenGradientBg}
          style={{ position: 'absolute', height: 900, left: 0, top: 0, width: '100%' }}
        />
        <div className={styles.greenEllipse} />
      </>

      <PageLayout
        childrenStyle={{ paddingTop: 30 }}
        dashedBorder
        examName="ACT"
        navigationItems={props.navigationItems}
        notice={props.notice}
      >

        <HeroSection
          examName="ACT"
          header="ACT Score Improvement Made Easy"
          subHeader="7-Day Money Back Guarantee: Email us if you want to cancel for any reason. No strings attached."
          valueProps={[
            {
              description: "22 official, full-length practice tests directly from ACT",
              icon: PracticeTestsIcon,
              name: "Practice Tests"
            },
            {
              description: "Learn from your mistakes with a text or video explanation to every question",
              icon: PracticeQuestionsIcon,
              name: "5000+ Practice Questions"
            },
            {
              description: "Prepare for every section, on any device and 100% online",
              icon: VideoLessonsIcon,
              name: "Expert Video Lessons"
            },
            {
              description: "Proven study schedules that have worked for thousands of students",
              icon: StudySchedulesIcon,
              name: "Proven Study Schedules"
            },
            {
              description: "Never get stuck with an AI tutor to explain any question or concept",
              icon: AITutorIcon,
              name: "AI Tutor"
            },
            {
              description: "Be confident when you're ready with our score predictor tool",
              icon: ScorePredictorIcon,
              name: "Accurate Score Predictor"
            }
          ]}
        />

        <OffersSection
          primaryOffer={{
            header: (
              <>
                <span className="text-bold text-white">Premium</span>
                {' '}<span style={{ color: '#8E70B2' }}>+</span>{' '}
                <span className="text-bold text-white">On-Demand Classes</span>
              </>
            ),
            highlight: <small>Works for <strong>every learning style</strong></small>,
            offer: onDemandClassesOffer,
            subHeader: 'Guided study for more structure',
            valuePropGroups: [
              {
                content: [
                  {
                    content: <><strong>12 months of access</strong> to Magoosh ACT Prep premium content and features</>,
                    tooltip: 'Access to all premium content and features<br />included in Magoosh ACT prep'
                  }
                ],
                header: 'Content'
              },
              {
                content: [
                  {
                    content: <strong>Enhanced ACT ready</strong>,
                    tooltip: 'Questions, lessons, and practice tests are all<br />updated for the new Enhanced ACT.'
                  }
                ],
                header: 'Features'
              },
              {
                content: [
                  {
                    content: <>Engaging classes led by a <strong>99th-percentile ACT instructor</strong></>,
                    tooltip: 'Our ACT expert takes you in-depth on the<br />topics you need to know'
                  },
                  {
                    content: <><strong>16 hours</strong> of supplemental instruction on key topics</>,
                    tooltip: 'Great for students with limited time.<br/>These classes cover the essentials as well as<br />the best opportunities to improve your score.'
                  },
                  {
                    content: <a className="text-white" href="/about-on-demand-classes">Learn more here</a>
                  }
                ],
                header: 'On-Demand Classes'
              }
            ]
          }}
          secondaryOffer={{
            header: (
              <>
                <span className="text-bold text-gray-darker">Premium</span>
                {' '}<span style={{ color: '#9BBCAF' }}>·</span>{' '}
                <span className="text-bold text-gray-darker">12 months</span>
              </>
            ),
            offer: premiumOffer,
            subHeader: 'Self-paced lessons, questions, and practice tests',
            valuePropGroups: [
              {
                content: [
                  {
                    content: <>Built from <strong>official ACT® questions</strong></>,
                    tooltip: "Over 3800 official ACT® questions<br />created by ACT and used under license"
                  },
                  {
                    content: <>Over <strong>5,000 practice questions</strong></>,
                    tooltip: 'Train with every type of question,<br />every subject, and every difficulty level<br />— again and again'
                  },
                  {
                    content: <><strong>Video or text explanations</strong> for every question</>,
                    tooltip: 'Every question has its own <br />video or text explanations detailing wrong answers'
                  },
                  {
                    content: <>Up to <strong>22 full-length, official practice tests</strong></>,
                    tooltip: 'Official, full-length tests directly from ACT.'
                  },
                  {
                    content: <>Over <strong>250 video lessons</strong></>,
                    tooltip: 'Video lessons that will teach you every<br />concept tested on the ACT'
                  },
                  {
                    content: 'Curated study schedules',
                    tooltip: 'Expert designed prep schedules and<br />checklists to keep you on track'
                  }
                ],
                header: 'Content'
              },
              {
                content: [
                  {
                    content: <strong>Enhanced ACT ready</strong>,
                    tooltip: 'Questions, lessons, and practice tests are all<br />updated for the new Enhanced ACT.'
                  },
                  {
                    content: <>+7-Day <strong>money-back guarantee</strong></>,
                    tooltip: 'Email us if you want to cancel for any<br />reason. No strings attached'
                  },
                  {
                    content: 'Online help from expert ACT tutors',
                    tooltip: 'Conveniently message our academic support<br />team whenever you have a question or need some help'
                  },
                  {
                    content: 'Data-driven score prediction',
                    tooltip: 'We compare your performance on practice<br />questions with the data of thousands of others to<br />estimate your score—and refine as you keep<br />practicing'
                  },
                  {
                    content: 'Smart progress tracking',
                    tooltip: 'Track your strengths and weaknesses as<br />you practice. Set up a weekly study planner and text<br />reminders to keep you focused.'
                  }
                ],
                header: 'Features'
              }
            ]
          }}
          tertiaryOffer={null}
          trialOffer={{
            header: 'Try Magoosh for 7 days with no commitment',
            offer: trialOffer,
            subHeader: 'Sign up for a free trial and get to know some of our lesson videos and practice questions.'
          }}
        />

        <CompetitorComparisonSection
          companyFeatureOfferings={[
            {
              name: 'Magoosh',
              'Price': formatPrice(premiumOffer.fullPriceInCents, 'usd', true),
              'Length of Online Access': '12 months',
              'Official ACT Questions': 'Included',
              'AI Tutor': 'Included',
              'Practice Questions and Exams': 'Included',
              'Mobile App': 'Included',
              'Free Trial': 'Included',
              'Video Lessons': 'Included',
              'Video & Text Question Explanations': 'Included',
              'No Questions Asked Money-Back Guarantee': 'Included',
              'Score-Improvement Guarantee': '+4 Point Increase Guarantee',
              'Study Plans': 'Included',
              'Flashcards Included': 'Included',
              'Score Predictor': 'Included',
              'Quiz Mode & Customized Practice': 'Included'
            },
            {
              name: 'Acely',
              'Price': '$588',
              'Length of Online Access': '12 months',
              'AI Tutor': 'Included',
              'Free Trial': 'Included',
              'Score-Improvement Guarantee': '+1 Point Increase Guarantee',
              'Study Plans': 'Included',
              'Score Predictor': 'Included',
            },
            {
              name: 'Princeton Review',
              'Price': '$299',
              'Length of Online Access': '12 months',
              'Practice Questions and Exams': 'Included',
              'Video Lessons': 'Included',
              'Score-Improvement Guarantee': '+1 Point Increase Guarantee',
              'Quiz Mode & Customized Practice': 'Included'
            },
            {
              name: 'PrepScholar',
              'Price': '$495',
              'Length of Online Access': '12 months',
              'AI Tutor': 'Included',
              'Practice Questions and Exams': 'Included',
              'Free Trial': 'Included',
              'Video Lessons': 'Included',
              'Score-Improvement Guarantee': '+4 Point Increase Guarantee',
              'Study Plans': 'Included',
              'Quiz Mode & Customized Practice': 'Included'
            }
          ]}
          features={[
            { icon: DollarSignIcon, name: 'Price' },
            { icon: HourglassIcon, name: 'Length of Online Access' },
            { icon: BannerCheckIcon, name: 'Official ACT Questions' },
            { icon: StarIcon, name: 'AI Tutor' },
            { icon: BubbleQuestionIcon, name: 'Practice Questions and Exams' },
            { icon: MobileDeviceIcon, name: 'Mobile App' },
            { icon: FireworkIcon, name: 'Free Trial' },
            { icon: VideoPlayerIcon, name: 'Video Lessons' },
            { icon: NotebookIcon, name: 'Video & Text Question Explanations' },
            { icon: CircleRefreshIcon, name: 'No Questions Asked Money-Back Guarantee' },
            { icon: HighFiveIcon, name: 'Score-Improvement Guarantee' },
            { icon: ChecklistIcon, name: 'Study Plans' },
            { icon: FlashcardsIcon, name: 'Flashcards Included' },
            { icon: ScoreGaugeIcon, name: 'Score Predictor' },
            { icon: StarIcon, name: 'Quiz Mode & Customized Practice' }
          ]}
          registrationUrl={defaultRegistrationUrl(premiumOffer)}
        />

        <TestimonialsSection />

        <OnDemandClassesSection />

        <FAQSection
          entries={[
             {
              answer: (
                <>
                  <p>Yes! Our lessons, practice questions, and practice tests have already been updated to reflect the <a href="/enhanced-act">changes to the ACT</a>.</p>
                </>
              ),
              question: 'Can I use Magoosh to prepare for the Enhanced ACT?'
            },
            {
              answer: "Nope! Previously having taken the test is not required. Many of our students are first-time ACT test-takers, so if you're ready to start studying now, you should go ahead and sign up! Our lessons will teach you everything you need to know about the ACT, and our instructors go in-depth on topics you may not have fully covered in your classes.",
              question: 'Do I need to have taken the test before?'
            },
            {
              answer: (
                <>
                  <p>Anyone can use Magoosh! Our prep works best for students who fall into one of the following categories:</p>
                  <ul>
                    <li><strong>Busy families:</strong> those who don't have the time or flexibility in their schedule for in person tutoring.</li>
                    <li><strong>Self-studiers:</strong> students who prefer to prep at their own pace with tutoring support as needed.</li>
                    <li><strong>Video-lovers:</strong> students who learn better by watching videos than by reading. Most Magoosh ACT practice questions have a video explanation teaching you how to solve it.</li>
                  </ul>
                </>
              ),
              question: 'Who should use Magoosh?'
            },
            {
              answer: (
                <>
                  We guarantee after completing our premium program, you'll see a 4 point increase over your old scores (as long as you scored a 30 or below), or we'll give you all of your money back. <a href="/score-guarantee">Click here for details</a>.
                </>
              ),
              question: 'How does the score guarantee work?'
            },
            {
              answer: (
                <>It’s 100% online. No DVDs. No books. It's all online. It works on both Macs and PCs: any device with an internet connection! You can also study on your Android or iOS device with our <a href="/act-apps">ACT Exam Prep & Practice App</a> or on your mobile browser</>
              ),
              question: 'How does the course work?'
            },
            {
              answer: "If you're unhappy for any reason, email us within 7 days of purchasing, and we will give you a full refund. No strings attached.",
              question: 'How does the 7-day refund policy work?'
            },
            {
              answer: "Of course! We are happy to help you with any concerns or questions you have about any of our material. Send us a message while you're studying, and one of our ACT tutors will get back to you quickly.",
              question: "If I'm struggling with a question, can I get help?"
            },
            {
              answer: (
                <>
                  <p>If you choose to pay in four monthly installments, your first payment is due at checkout, with the remaining installments charged monthly. You'll receive an email with your payment schedule after checkout. </p><br />
                  <p><strong>Please note:</strong> Installment plans are not subscriptions—payments continue even if you pause your plan, but your access will be extended by the paused duration. By choosing an installment plan, you are committing to paying the full purchase amount unless you request a refund within 7 days of your initial purchase. Pausing your plan does not extend the refund window.</p>
                </>
              ),
              question: 'How do installment payments work?'
            },
            {
              answer: "No. Magoosh plans do not renew or auto-renew. You purchase a plan for a set length of time, and when it ends your access simply expires — there's nothing to cancel. If you'd like more time, you can purchase a new plan.",
              question: 'Do Magoosh plans renew?'
            },
            {
              answer: (
                <>The study tracker on your dashboard helps keep track of the amount of material you've worked on and how many days per week you've studied for the ACT. For advance planning, you can follow one of our expert-curated <a href="https://magoosh.com/hs/act-study-schedules">study schedules</a>.</>
              ),
              question: 'How does the study planner work?'
            },
            {
              answer: 'Visa, Mastercard, American Express, Discover, and PayPal. We process our transactions through Paypal, so your credit card info is safe.',
              question: 'What types of payment do you accept?'
            },
            {
              answer: (
                <>Our platform is designed to be used by one student on each account to ensure accurate scoring. If you have more than one student in your family taking the SAT or ACT you may be eligible for a discount — please email us at <a href="mailto:help@magoosh.com">help@magoosh.com</a> before signing up and we'll take care of you.</>
              ),
              question: 'There are multiple students in my family. Do I need to purchase separate accounts for each of them?'
            },
            {
              answer: (
                <>
                  We'd love to have you study with us again. If you've purchased any other Magoosh plan, email us at <a href="mailto:help@magoosh.com">help@magoosh.com</a> and we'll help you out.
                </>
              ),
              question: "I'm a Magoosh student studying for the SAT. Can I have a discount if I add a Magoosh ACT plan?"
            },
            {
              answer: (
                <>Email us at <a href="mailto:help@magoosh.com">help@magoosh.com</a> and we can help you out! :)</>
              ),
              question: "I'm a sophomore or junior and I need access for over a year!"
            },
            {
              answer: (
                <>You definitely can! Email us at <a href="mailto:help@magoosh.com">help@magoosh.com</a> and we'll work something out with you.</>
              ),
              question: 'Can I purchase SAT and ACT prep together?'
            },
            {
              answer: (
                <>Yes! You can visit our <a href="https://schools.magoosh.com">Educators page</a> to learn more.</>
              ),
              question: 'Do you have resources or prep offerings for educators?'
            }
          ]}
        />

        <StartYourPrepSection
          examName="ACT"
          pointIncrease={4}
          primaryOffer={{ ...onDemandClassesOffer, name: 'Premium + On-Demand Classes' }}
          secondaryOffer={{ ...premiumOffer, name: 'Premium Only' }}
          trialOffer={trialOffer}
        />
      </PageLayout>
    </div>
  );
};
